import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { sanitizeMath } from '../../utils/mathText';
import {
  AddRounded,
  AddPhotoAlternateRounded,
  AutoAwesomeRounded,
  CloseRounded,
  DeleteOutlineRounded,
  HistoryRounded,
  MicRounded,
  SendRounded,
  StopRounded,
} from '@mui/icons-material';
import { Student } from '../../types';
import { useTeacherAuth } from '../../context/TeacherAuthContext';
import { resolveTeacherClassContext } from '../../utils/teacherClassContext';
import { downscaleImage } from '../../utils/image';
import {
  askCoach,
  uploadCoachImage,
  listCoachChats,
  getCoachChat,
  deleteCoachChat,
  CoachChatMessage,
  TeacherChatSummary,
  transcribeAudioAsTeacher,
} from '../../services/api';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import TeacherLayout from './TeacherLayout';
import LessonPlanner from './LessonPlanner';
import LanguageToggle from '../common/LanguageToggle';
import { GeneratedImageBlock } from '../ChatMessage';

/**
 * Ask the coach — teacher chat screen.
 *
 * Every send calls POST /api/teachers/me/coach-chat, which grounds the reply
 * in the class's real data: both curricula (NCERT coverage and the teacher's
 * own Global syllabus), homework/exams, and where students struggle. Arriving
 * with `location.state.student` (from "Ask coach about {name}") focuses the
 * conversation on that student until the chip is dismissed.
 */

type Role = 'teacher' | 'coach';

interface ChatMessage {
  id: string;
  role: Role;
  content: string;
  /** Renders the bubble in a muted "thinking" style. */
  pending?: boolean;
  /** The coach ran out of room and the server couldn't finish the reply even
   *  after continuing it. Shown as incomplete so nobody teaches from half a
   *  plan believing it's the whole thing. */
  truncated?: boolean;
  /** An image the teacher attached to this turn (rendered in the bubble). */
  imageUrl?: string;
}

const CLASS_STARTERS: { label: string; prompt: string }[] = [
  {
    label: 'Plan next week',
    prompt: 'Plan next week for my class from where my syllabus is right now.',
  },
  {
    label: 'Revision plan for the strugglers',
    prompt: 'Make a simple revision plan for the students who are behind.',
  },
  {
    label: "Draft this week's homework",
    prompt: 'Draft homework from what we covered this week.',
  },
  {
    label: 'Summarise the class',
    prompt: 'Summarise how the class is doing, in plain words.',
  },
];

const studentStarters = (name: string): { label: string; prompt: string }[] => [
  { label: `Make a plan for ${name}`, prompt: `Make a simple plan for ${name}.` },
  { label: 'Help with the gaps', prompt: `How do I help ${name} with the gaps?` },
  { label: 'What to revise', prompt: `What should ${name} revise this week?` },
  { label: `Summarise ${name}`, prompt: `Summarise ${name} in plain words.` },
];

const initials = (name?: string | null) => {
  if (!name) return 'T';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'T';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const CoachAvatar: React.FC = () => (
  <span
    aria-hidden="true"
    className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 text-white"
  >
    <AutoAwesomeRounded sx={{ fontSize: 17 }} />
  </span>
);

interface AskCoachLocationState {
  student?: Student;
  school?: string;
  grade?: number | string;
  section?: string | null;
}

const AskCoach: React.FC = () => {
  const location = useLocation();
  const { teacher, token, language, setLanguage } = useTeacherAuth();
  const locState = (location.state as AskCoachLocationState) || {};

  const ctx = useMemo(() => resolveTeacherClassContext(locState), [locState]);
  const grade =
    typeof ctx.grade === 'number' ? ctx.grade : ctx.grade ? Number(ctx.grade) : undefined;
  const section = ctx.section ?? null;

  const classLabel =
    grade != null && !Number.isNaN(grade)
      ? `Grade ${grade}${section ? ` · ${section}` : ''}`
      : 'Your class';

  // "Ask coach about {name}" — a dismissable focus on one student.
  const [focusStudent, setFocusStudent] = useState<Student | null>(locState.student ?? null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  // Persistence: the saved chat this conversation belongs to (created server-
  // side on the first reply), the teacher's chat history, and the panel toggle.
  const [chatId, setChatId] = useState<number | null>(null);
  const [savedChats, setSavedChats] = useState<TeacherChatSummary[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [planning, setPlanning] = useState(false);
  // Image the teacher is attaching to the next turn (vision).
  const [attachedImage, setAttachedImage] = useState<Blob | null>(null);
  const [attachedPreview, setAttachedPreview] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  // Voice input — same teacher-authed STT as the weekly-plan voice capture.
  // The transcript lands in the box (appended) so the teacher can fix it
  // before sending, rather than auto-sending a possibly misheard question.
  const {
    isSupported: isVoiceSupported,
    isRecording,
    isTranscribing,
    error: voiceError,
    toggleRecording,
    stopRecording,
  } = useVoiceRecorder({
    onResult: (text) => {
      setInput((prev) => (prev.trim() ? `${prev.trimEnd()} ${text}` : text));
      inputRef.current?.focus();
    },
    transcribe: (blob, name) => transcribeAudioAsTeacher(blob, name),
  });

  useEffect(() => {
    if (voiceError) setNote(voiceError);
  }, [voiceError]);

  // Don't keep the mic open while a reply is being generated.
  useEffect(() => {
    if (sending && isRecording) stopRecording();
  }, [sending, isRecording, stopRecording]);

  const clearAttachedImage = () => {
    if (attachedPreview) URL.revokeObjectURL(attachedPreview);
    setAttachedImage(null);
    setAttachedPreview(null);
  };

  const handlePickImage = async (file: File) => {
    try {
      const blob = await downscaleImage(file);
      if (attachedPreview) URL.revokeObjectURL(attachedPreview);
      setAttachedImage(blob);
      setAttachedPreview(URL.createObjectURL(blob));
    } catch {
      setNote('Could not read that image. Try another one.');
    }
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMsgRef = useRef<HTMLDivElement>(null);
  const teacherInitials = initials(teacher?.name || teacher?.username);

  // The messages scroll inside their own pane (header + composer stay fixed).
  // While the coach is "Thinking…" (or you just sent), keep the bottom in view;
  // once a reply is DONE, jump to the START of that reply so you read it from
  // the top, not the end.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const last = messages[messages.length - 1];
    if (last && last.role === 'coach' && !last.pending && lastMsgRef.current) {
      lastMsgRef.current.scrollIntoView({ block: 'start' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const starters = focusStudent ? studentStarters(focusStudent.first_name) : CLASS_STARTERS;

  // Saved chats for this class (newest first).
  const refreshChatList = React.useCallback(() => {
    if (!token || grade == null || Number.isNaN(Number(grade))) return;
    listCoachChats(token, Number(grade), section).then(setSavedChats);
  }, [token, grade, section]);
  useEffect(() => {
    refreshChatList();
  }, [refreshChatList]);

  // New chat: clear the thread (a fresh one is saved on the first reply).
  const startNewChat = () => {
    setMessages([]);
    setChatId(null);
    setFocusStudent(null);
    setNote(null);
    setShowHistory(false);
    inputRef.current?.focus();
  };

  // Reopen a saved chat.
  const openChat = async (id: number) => {
    if (!token) return;
    try {
      const chat = await getCoachChat(token, id);
      setMessages(
        // `truncated` is carried through so a reply that never finished still
        // says so after a reload — otherwise the warning and the Continue
        // button disappear and half a plan reads as the whole thing.
        (chat.messages || []).map((m, i) => ({
          id: `s-${id}-${i}`,
          role: m.role as Role,
          content: m.content,
          truncated: m.truncated,
        }))
      );
      setChatId(chat.id);
      setFocusStudent(null);
      setShowHistory(false);
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Failed to load that chat.');
    }
  };

  const removeChat = async (id: number) => {
    if (!token) return;
    try {
      await deleteCoachChat(token, id);
      setSavedChats((prev) => prev.filter((c) => c.id !== id));
      if (chatId === id) startNewChat();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Failed to delete that chat.');
    }
  };

  // Clicking a starter fills the composer — it does NOT send.
  const fillFromStarter = (prompt: string) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  // Send a turn to the coach. Grounds the reply server-side in the class's
  // real data (and the focus student, while the chip is active). `text` comes
  // from the composer on a normal send, or is passed directly by the "Continue
  // the reply" button on a reply that was cut short.
  const send = async (text: string) => {
    if ((!text && !attachedImage) || sending) return;
    if (!token || grade == null || Number.isNaN(grade)) {
      setNote('Pick a class first so the curiosity knows who you mean.');
      return;
    }

    const imageToSend = attachedImage;
    const previewToShow = attachedPreview;
    // A turn needs some text for the model; default when it's image-only.
    const teacherText = text || 'Can you take a look at this image?';

    const history: CoachChatMessage[] = messages
      .filter((m) => !m.pending && m.content.trim())
      .map((m) => ({ role: m.role, content: m.content }));
    history.push({ role: 'teacher', content: teacherText });

    const now = Date.now();
    const pendingId = `c-${now}`;
    setMessages((prev) => [
      ...prev,
      { id: `t-${now}`, role: 'teacher', content: teacherText, imageUrl: previewToShow || undefined },
      { id: pendingId, role: 'coach', content: 'Thinking…', pending: true },
    ]);
    setInput('');
    setAttachedImage(null);
    setAttachedPreview(null);
    setNote(null);
    setSending(true);

    try {
      let attachedImageId: string | undefined;
      if (imageToSend) {
        const up = await uploadCoachImage(token, imageToSend);
        attachedImageId = up.image_id;
      }
      const res = await askCoach(token, {
        grade: Number(grade),
        section,
        messages: history,
        student_id: focusStudent?.id,
        chat_id: chatId,
        attached_image_id: attachedImageId,
      });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === pendingId
            ? { ...m, content: res.reply, pending: false, truncated: res.truncated }
            : m
        )
      );
      if (res.chat_id != null) setChatId(res.chat_id);
      refreshChatList();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'The curiosity is unavailable right now. Please try again.';
      setMessages((prev) =>
        prev.map((m) => (m.id === pendingId ? { ...m, content: msg, pending: false } : m))
      );
    } finally {
      setSending(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    await send(input.trim());
  };

  return (
    <TeacherLayout active="chat" fillViewport>
      <div className="flex min-h-0 flex-1 flex-col pb-16 md:pb-3">
      {/* FIXED TOP — header, chats and chips stay put while only the chat scrolls */}
      <div className="flex-none">
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-slate-400">
            {classLabel}
          </p>
          <h1 className="mt-1 text-[clamp(1.3rem,3.6vw,2rem)] font-bold tracking-tight text-slate-900 md:mt-2">
            Ask the curiosity
          </h1>
          {/* Description is informational — hidden on phones to give the chat more room. */}
          <p className="mt-1.5 hidden max-w-[62ch] text-[0.96rem] text-slate-500 md:block">
            Plan revision, draft homework, learn a concept yourself, or ask how to teach a
            tricky topic. The curiosity knows what this class has covered.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <LanguageToggle value={language} onChange={setLanguage} />
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-[#E9E6F4] bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-violet-400 hover:text-violet-700"
          >
            <HistoryRounded sx={{ fontSize: 17 }} /> Chats
          </button>
          <button
            type="button"
            onClick={startNewChat}
            className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700"
          >
            <AddRounded sx={{ fontSize: 17 }} /> New chat
          </button>
        </div>
      </div>

      {/* Saved chats panel */}
      {showHistory && (
        <div className="mb-4 rounded-2xl border border-[#E9E6F4] bg-white p-3">
          {savedChats.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-slate-500">No saved chats yet — your conversations are saved automatically once the curiosity replies.</p>
          ) : (
            <ul className="flex flex-col">
              {savedChats.map((c) => (
                <li
                  key={c.id}
                  className={`flex items-center gap-2 rounded-xl px-2 py-1 ${c.id === chatId ? 'bg-violet-50' : 'hover:bg-slate-50'}`}
                >
                  <button
                    type="button"
                    onClick={() => openChat(c.id)}
                    className="min-w-0 flex-1 truncate py-1.5 text-left text-sm font-medium text-slate-700 hover:text-violet-700"
                  >
                    {c.title || 'Untitled chat'}
                    <span className="ml-2 text-xs font-normal text-slate-400">
                      {c.message_count} msgs{c.updated_at ? ` · ${new Date(c.updated_at).toLocaleDateString()}` : ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeChat(c.id)}
                    aria-label={`Delete chat ${c.title || c.id}`}
                    className="flex-none rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                  >
                    <DeleteOutlineRounded sx={{ fontSize: 17 }} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

        {/* Starter chips — clicking one fills the composer (does not send). One
            horizontally-scrollable row on phones, wrapping grid on wider screens. */}
        <div className="mb-3 flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-x-visible md:pb-0">
          <button
            type="button"
            onClick={() => setPlanning((v) => !v)}
            className={`inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-xl px-3.5 py-2.5 text-[0.83rem] font-semibold transition-colors ${
              planning
                ? 'bg-violet-600 text-white'
                : 'border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100'
            }`}
          >
            <AutoAwesomeRounded sx={{ fontSize: 16 }} /> Plan a session
          </button>
          {starters.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => fillFromStarter(s.prompt)}
              className="flex-none whitespace-nowrap rounded-xl border border-[#E9E6F4] bg-white px-3.5 py-2.5 text-[0.83rem] font-semibold text-slate-900 transition-colors hover:border-violet-500 hover:text-violet-700"
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>{/* end FIXED TOP */}

      {/* SCROLL REGION — only the conversation scrolls */}
      <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
        {/* "Mind Jog" session planner (guided flow). */}
        {planning && (
          grade != null && !Number.isNaN(grade) ? (
            <LessonPlanner
              token={token || ''}
              grade={Number(grade)}
              section={section}
              onClose={() => setPlanning(false)}
            />
          ) : (
            <p className="text-[0.85rem] text-slate-500">Pick a class first to plan a session.</p>
          )
        )}

        {/* Empty state — tells the teacher everything this chat can do */}
        {messages.length === 0 && (
          <div className="flex gap-2.5 rounded-2xl border border-[#E9E6F4] bg-white px-5 py-4">
            <CoachAvatar />
            <div className="text-[0.94rem] leading-relaxed text-slate-600">
              <p className="font-semibold text-slate-800">
                I'm your curiosity — I know your class inside out. You can:
              </p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5">
                <li>
                  <span className="font-medium text-slate-700">Plan</span> — revision plans, next
                  week, homework drafts, grounded in what you've actually covered in both syllabi.
                </li>
                <li>
                  <span className="font-medium text-slate-700">Ask about any student by name</span>{' '}
                  — "how is Ravi doing?" gets their real progress, curriculum-wise.
                </li>
                <li>
                  <span className="font-medium text-slate-700">Learn something yourself</span> —
                  "explain electromagnetic induction to me before tomorrow's class."
                </li>
                <li>
                  <span className="font-medium text-slate-700">Ask how the app works</span> — how
                  to tick the syllabus, set exams, or make the curiosity reinforce a topic with a
                  student.
                </li>
              </ul>
              <p className="mt-1.5 text-[0.85rem] text-slate-400">
                Chats save automatically — find them under "Chats" above.
                {focusStudent ? ` Right now we're talking about ${focusStudent.first_name}.` : ''}
              </p>
            </div>
          </div>
        )}

        {/* Chat log */}
        <div className="flex flex-col gap-3.5">
          {messages.map((m, i) =>
            m.role === 'teacher' ? (
              <div
                key={m.id}
                ref={i === messages.length - 1 ? lastMsgRef : undefined}
                className="ml-auto flex max-w-[88%] flex-row-reverse gap-2.5"
              >
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-violet-100 text-[0.72rem] font-bold text-violet-700"
                >
                  {teacherInitials}
                </span>
                <div className="rounded-[15px] rounded-tr-[5px] bg-violet-600 px-4 py-3.5 text-[0.94rem] leading-relaxed text-white">
                  {m.imageUrl && (
                    <img
                      src={m.imageUrl}
                      alt="Attached"
                      className="mb-2 max-h-48 rounded-lg object-cover"
                    />
                  )}
                  {m.content}
                </div>
              </div>
            ) : (
              <div
                key={m.id}
                ref={i === messages.length - 1 ? lastMsgRef : undefined}
                className="flex max-w-[88%] scroll-mt-2 gap-2.5"
              >
                <CoachAvatar />
                <div
                  className={`rounded-[15px] rounded-tl-[5px] border px-4 py-3.5 text-[0.94rem] leading-relaxed ${
                    m.pending
                      ? 'border-dashed border-violet-200 bg-violet-50 italic text-violet-700'
                      : 'border-[#E9E6F4] bg-white text-slate-900'
                  }`}
                >
                  {m.pending ? (
                    <span>{m.content}</span>
                  ) : (
                    <div className="space-y-2 [&_h2]:font-bold [&_h3]:mt-2 [&_h3]:font-bold [&_li]:my-0.5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-0 [&_strong]:font-semibold [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_.katex-display]:my-1 [&_.katex-display]:overflow-x-auto">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[[rehypeKatex, { strict: 'ignore', throwOnError: false, errorColor: '#9CA3AF', output: 'html' }]]}
                        components={{
                          img: ({ src, alt }: any) => (
                            <GeneratedImageBlock src={String(src || '')} alt={alt || ''} />
                          ),
                        }}
                      >
                        {sanitizeMath(m.content)}
                      </ReactMarkdown>
                    </div>
                  )}
                  {m.truncated && !m.pending && (
                    <div className="mt-3 border-t border-amber-200 pt-2.5">
                      <p className="text-[0.82rem] font-medium text-amber-700">
                        This reply was cut short before it finished.
                      </p>
                      {/* Reopened chats can surface a cut-short reply from the
                          middle of the conversation, and "continue where you
                          stopped" only means anything at the end — asking it of
                          an older reply would carry on the newest one instead.
                          The notice stays either way; only the button is tied to
                          the last turn. */}
                      {i === messages.length - 1 && (
                        <button
                          type="button"
                          onClick={() => void send('Please continue from where you stopped.')}
                          disabled={sending}
                          className="mt-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-[0.82rem] font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                        >
                          Continue the reply
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </div>

        {note && (
          <p className="text-[0.82rem] font-medium text-slate-400" role="status">
            {note}
          </p>
        )}

        {/* Focus-student chip — the coach grounds replies in this student. */}
        {focusStudent && (
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 py-1 pl-3 pr-1.5 text-[0.8rem] font-semibold text-violet-700">
              About {focusStudent.first_name}
              <button
                type="button"
                aria-label={`Stop talking about ${focusStudent.first_name}`}
                onClick={() => setFocusStudent(null)}
                className="flex h-5 w-5 items-center justify-center rounded-full text-violet-500 transition-colors hover:bg-violet-200 hover:text-violet-800"
              >
                <CloseRounded sx={{ fontSize: 14 }} />
              </button>
            </span>
          </div>
        )}

      </div>{/* end SCROLL REGION */}

      {/* Attached-image preview strip */}
      {attachedPreview && (
        <div className="mt-3 flex items-center gap-2.5 rounded-[12px] border border-[#E9E6F4] bg-white px-3 py-2">
          <img src={attachedPreview} alt="Attached preview" className="h-12 w-12 flex-none rounded-lg object-cover" />
          <span className="flex-1 truncate text-[0.85rem] text-slate-600">Image attached — send with your question</span>
          <button
            type="button"
            aria-label="Remove image"
            onClick={clearAttachedImage}
            className="flex h-7 w-7 flex-none items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <CloseRounded sx={{ fontSize: 16 }} />
          </button>
        </div>
      )}

      {/* Composer — fixed under the scrolling messages */}
      <form
        onSubmit={handleSend}
        className="mt-3 flex flex-none items-center gap-2.5 rounded-[15px] border border-[#E9E6F4] bg-white py-2 pl-3 pr-2"
      >
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handlePickImage(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            aria-label="Attach an image"
            disabled={sending}
            onClick={() => imageInputRef.current?.click()}
            className="flex h-10 w-10 flex-none items-center justify-center rounded-[11px] bg-violet-50 text-violet-700 transition-colors hover:bg-violet-100 disabled:opacity-50"
          >
            <AddPhotoAlternateRounded sx={{ fontSize: 18 }} />
          </button>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={sending}
            placeholder="Ask about a study plan, homework, or how to teach something…"
            aria-label="Message the curiosity"
            className="flex-1 border-0 bg-transparent text-[0.95rem] text-slate-900 outline-none placeholder:text-slate-400 disabled:opacity-60"
          />
          {isVoiceSupported && (
            <button
              type="button"
              aria-label={isRecording ? 'Stop recording' : isTranscribing ? 'Transcribing' : 'Speak'}
              title={isRecording ? 'Listening… tap to stop' : isTranscribing ? 'Transcribing…' : 'Speak your question'}
              disabled={sending || isTranscribing}
              onClick={() => {
                setNote(null);
                toggleRecording();
              }}
              className={`flex h-10 w-10 flex-none items-center justify-center rounded-[11px] transition-colors disabled:opacity-50 ${
                isRecording
                  ? 'animate-pulse bg-rose-500 text-white hover:bg-rose-600'
                  : 'bg-violet-50 text-violet-700 hover:bg-violet-100'
              }`}
            >
              {isTranscribing ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-violet-300 border-t-violet-700" />
              ) : isRecording ? (
                <StopRounded sx={{ fontSize: 18 }} />
              ) : (
                <MicRounded sx={{ fontSize: 18 }} />
              )}
            </button>
          )}
          <button
            type="submit"
            aria-label="Send"
            disabled={sending || (!input.trim() && !attachedImage)}
            className="flex h-10 w-10 flex-none items-center justify-center rounded-[11px] bg-violet-600 text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SendRounded sx={{ fontSize: 18 }} />
          </button>
        </form>
      </div>
    </TeacherLayout>
  );
};

export default AskCoach;
